import {
  Button,
  Caption,
  Input,
  Label,
  QrCodeScannerIcon,
  Spinner,
  toast,
} from "@mychili/clarity-ui";
import { useRef, useState } from "react";
import { Html5QrcodePlugin } from "../../features/qr-scanner";
import { type MerchantsTypes, merchantsApi } from "../../shared/api/client";
import { getNetworkErrorMessage } from "../../shared/api/utils/get-network-error-message";

type SaveData =
  MerchantsTypes.components["schemas"]["SaveDuitnowMerchantResponse"];

type Step =
  | { kind: "idle" }
  | { kind: "scanning" }
  | { kind: "scanned"; merchantName: string; qrCode: string }
  | { kind: "saving"; merchantName: string; qrCode: string }
  | { kind: "save_success"; data: SaveData }
  | {
      kind: "save_failed";
      merchantName: string;
      brn: string;
      storeAddress: string;
    }
  | { kind: "scan_failed"; message: string };

const MerchantCard = ({
  name,
  brn,
  storeAddress,
}: {
  name?: string;
  brn?: string;
  storeAddress?: string;
}) => (
  <div className="flex flex-col items-center gap-1 rounded-xl bg-gray-50 p-6">
    <p className="typography-medium-text-semibold">{name}</p>
    <p className="typography-regular-text-regular text-text-neutral-subtler">
      BRN: {brn}
    </p>
    <p className="typography-regular-text-regular text-text-neutral-subtler">
      Store address: {storeAddress}
    </p>
  </div>
);

export const PageDuitnowTest = () => {
  const [step, setStep] = useState<Step>({ kind: "idle" });
  const [brn, setBrn] = useState("");
  const [storeAddress, setStoreAddress] = useState("");
  const isScanInProgress = useRef(false);

  const handleReset = () => {
    isScanInProgress.current = false;
    setStep({ kind: "idle" });
    setBrn("");
    setStoreAddress("");
  };

  const handleScanSuccess = async (qrCode: string) => {
    if (isScanInProgress.current) return;
    isScanInProgress.current = true;

    try {
      const { data: res } = await merchantsApi.POST(
        "/api/merchants-service/duitnow/qr",
        { body: { qr_code: qrCode } },
      );

      const qrData = res?.data;
      if (!qrData?.merchant_name) {
        setStep({ kind: "scan_failed", message: "Failed to parse QR code" });
        return;
      }

      setBrn(qrData.brn ?? "");
      setStep({ kind: "scanned", merchantName: qrData.merchant_name, qrCode });
    } catch (e) {
      isScanInProgress.current = false;
      const message = getNetworkErrorMessage(e);
      if (message) {
        setStep({ kind: "scan_failed", message });
      } else {
        toast.error("Failed to parse QR code");
      }
    }
  };

  const handleSave = async () => {
    if (step.kind !== "scanned") return;

    const { merchantName, qrCode } = step;
    setStep({ kind: "saving", merchantName, qrCode });

    try {
      const { data: res } = await merchantsApi.POST(
        "/api/merchants-service/duitnow/save",
        {
          body: {
            location: storeAddress,
            brn,
            merchant_name: merchantName,
            qr_code: qrCode,
          },
        },
      );

      if (!res?.data) throw new Error("empty response");
      setStep({ kind: "save_success", data: res.data });
    } catch {
      setStep({ kind: "save_failed", merchantName, brn, storeAddress });
    }
  };

  if (step.kind === "idle") {
    return (
      <div className="flex flex-col items-center gap-6 px-4 pt-8">
        <QrCodeScannerIcon className="h-20 w-20 text-gray-600" />
        <Button
          className="w-full"
          onClick={() => setStep({ kind: "scanning" })}
        >
          Add Duitnow QR
        </Button>
      </div>
    );
  }

  if (step.kind === "scanning") {
    return (
      <div className="flex flex-col gap-4 pt-8">
        <Html5QrcodePlugin
          fps={10}
          qrbox={250}
          disableFlip={false}
          onSuccess={handleScanSuccess}
        />
      </div>
    );
  }

  if (step.kind === "scanned" || step.kind === "saving") {
    return (
      <div className="flex flex-col items-center gap-4 px-4 pt-8">
        <p className="text-base">Merchant name:</p>
        <p className="font-bold text-xl uppercase">{step.merchantName}</p>

        <div className="flex w-full flex-col gap-4">
          <Label className="w-full">
            Enter Business Registration Number
            <Input
              className="w-full"
              variant="gray"
              clearable={false}
              disabled={step.kind === "saving"}
              value={brn}
              onChange={(e) => setBrn(e.target.value)}
            />
            <Caption className="mt-1">
              Instead of 202501028937 (1630349-P) fill in only what's in the
              brackets, w/o special characters, eg. 1630349P
            </Caption>
          </Label>
          <Label className="w-full">
            Enter Store Address
            <Input
              className="w-full"
              variant="gray"
              clearable={false}
              disabled={step.kind === "saving"}
              value={storeAddress}
              onChange={(e) => setStoreAddress(e.target.value)}
            />
          </Label>
        </div>

        <Button
          variant="secondary"
          disabled={
            step.kind === "saving" || !brn.trim() || !storeAddress.trim()
          }
          onClick={handleSave}
        >
          {step.kind === "saving" ? <Spinner size="s" /> : "Save"}
        </Button>

        <Button className="w-full" onClick={handleReset}>
          Add another Duitnow QR
        </Button>
      </div>
    );
  }

  if (step.kind === "save_success") {
    const { data } = step;
    return (
      <>
        <div className="bg-background-success-bold-hover p-10 text-center">
          <p className="typography-headline-h3-medium text-text-neutral-inverse">
            DuitNow QR successfully added!
          </p>
        </div>
        <div className="flex flex-col gap-4 px-4 pt-4">
          <MerchantCard
            name={data.name}
            brn={data.brn}
            storeAddress={data.location}
          />
          <Button className="w-full" onClick={handleReset}>
            Add new Duitnow QR
          </Button>
        </div>
      </>
    );
  }

  if (step.kind === "scan_failed") {
    return (
      <>
        <div className="bg-background-danger-bold-hover p-10 text-center">
          <p className="typography-headline-h3-medium text-text-neutral-inverse">
            {step.message}
          </p>
        </div>
        <div className="px-4 pt-4">
          <Button className="w-full" onClick={handleReset}>
            Add new Duitnow QR
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="bg-background-danger-bold-hover p-10 text-center">
        <p className="typography-headline-h3-medium text-text-neutral-inverse">
          Duitnow QR is NOT supported
        </p>
      </div>
      <div className="flex flex-col gap-4 px-4 pt-4">
        <MerchantCard
          name={step.merchantName}
          brn={step.brn}
          storeAddress={step.storeAddress}
        />
        <Button className="w-full" onClick={handleReset}>
          Add new Duitnow QR
        </Button>
      </div>
    </>
  );
};
